import React from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import { MarketingCopyProps } from '@components/MarketingModal/index';
import LeadCaptureModalWithNewsletter from '@components/MarketingModal/modals/Acquisition/P1';
import LeadCaptureModalWithNewsletters from '@components/MarketingModal/modals/Acquisition/P2';
import AppDownloadModal from '@components/MarketingModal/modals/AppDownloadModal';
import CtaButtonModal from '@components/MarketingModal/modals/CtaButtonModal';
import LeadCaptureModal from '@components/MarketingModal/modals/LeadCaptureModal';
import LeadCaptureModalSplitImg from '@components/MarketingModal/modals/LeadCaptureModalSplitImg';
import LeadCaptureModalWithHorizontalArticles from '@components/MarketingModal/modals/LeadCaptureModalWithHorizontalArticles';
import LeadCaptureModalWithMultipleSubscribeOptions from '@components/MarketingModal/modals/LeadCaptureModalWithMultipleSubscribeOptions/index';
import LeadCaptureModalWithSingleArticle from '@components/MarketingModal/modals/LeadCaptureModalWithSingleArticle';
import LeadCaptureModalWithVerticalArticles from '@components/MarketingModal/modals/LeadCaptureModalWithVerticalArticles';
import RetentionP1SingleContentHighlightText from '@components/MarketingModal/modals/Retention/P1';
import RetentionP2SingleContentHighlightImage from '@components/MarketingModal/modals/Retention/P2';
import { LeadTypes } from '@utils/enums';

export enum ModalType {
  AppDialog = 'app_dialog',
  CustomUrl = 'custom_url',
  LeadCapture = 'lead_capture',
  LeadCaptureModalWithVerticalArticles = 'lead_capture_modal_with_vertical_articles',
  LeadCaptureModalWithHorizontalArticles = 'lead_capture_modal_with_horizontal_articles',
  LeadCaptureWithSingleArticle = 'lead_capture_with_single_article',
  LeadCaptureWithSplitImg = 'lead_capture_with_split_img',
  LeadCaptureModalWithNewsletters = 'lead_capture_modal_with_newsletters',
  LeadCaptureModalWithNewsletter = 'lead_capture_modal_with_newsletter',
  LeadCaptureModalWithMultipleSubscribeOptions = 'lead_capture_modal_with_multiple_subscribe_options',
  RetentionP0 = 'retention_p0',
  RetentionP1 = 'retention_p1',
  RetentionP2 = 'retention_p2',
  StandardOffer = 'standard_offer',
}

const queryClient = new QueryClient();

const MarketingCopy = ({
  leadType = LeadTypes.MarketingModal,
  modalType,
  onClose,
  modalId,
  closeIconRef,
  ...restProps
}: MarketingCopyProps): JSX.Element => {
  const getModalByType = (modalType: ModalType) => {
    switch (modalType) {
      case ModalType.LeadCapture:
        return (
          <LeadCaptureModal
            leadType={leadType}
            modalType={modalType}
            modalId={modalId}
            {...restProps}
          />
        );
      case ModalType.LeadCaptureModalWithVerticalArticles:
        return (
          <LeadCaptureModalWithVerticalArticles
            leadType={leadType}
            modalType={modalType}
            modalId={modalId}
            {...restProps}
          />
        );
      case ModalType.LeadCaptureModalWithHorizontalArticles:
        return (
          <LeadCaptureModalWithHorizontalArticles
            modalType={modalType}
            leadType={leadType}
            modalId={modalId}
            {...restProps}
          />
        );
      case ModalType.LeadCaptureWithSingleArticle:
        return (
          <LeadCaptureModalWithSingleArticle
            leadType={leadType}
            modalType={modalType}
            modalId={modalId}
            {...restProps}
          />
        );
      case ModalType.LeadCaptureWithSplitImg:
        return (
          <LeadCaptureModalSplitImg leadType={leadType} modalType={modalType} {...restProps} />
        );
      case ModalType.LeadCaptureModalWithNewsletters:
        return (
          <LeadCaptureModalWithNewsletters
            leadType={leadType}
            modalType={modalType}
            modalId={modalId}
            closeIconRef={closeIconRef}
            {...restProps}
          />
        );
      case ModalType.LeadCaptureModalWithNewsletter:
        return (
          <LeadCaptureModalWithNewsletter
            leadType={leadType}
            modalType={modalType}
            modalId={modalId}
            {...restProps}
          />
        );
      case ModalType.LeadCaptureModalWithMultipleSubscribeOptions:
        return (
          <LeadCaptureModalWithMultipleSubscribeOptions
            leadType={leadType}
            modalType={modalType}
            modalId={modalId}
            closeIconRef={closeIconRef}
            {...restProps}
          />
        );
      case ModalType.CustomUrl:
      case ModalType.StandardOffer:
        return <CtaButtonModal modalType={modalType} {...restProps} />;
      case ModalType.AppDialog:
        return <AppDownloadModal onClose={onClose} />;
      case ModalType.RetentionP1:
        return <RetentionP1SingleContentHighlightText {...restProps} />;
      case ModalType.RetentionP2:
        return <RetentionP2SingleContentHighlightImage {...restProps} />;
      default:
        return null;
    }
  };

  return (
    <QueryClientProvider client={queryClient}>{getModalByType(modalType)}</QueryClientProvider>
  );
};

export default MarketingCopy;
